import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector, useAppDispatch } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { queryStringToObject, validationErrorsToString } from '../../utils';

// ICONS
import { ReactComponent as AlertIcon } from '../../assets/svg/alert.svg';
import { ReactComponent as CelebrateIcon } from '../../assets/svg/celebrate.svg';

// COMPONENTS
import LoadingSpinner from '../../components/LoadingSpinner';
import LoginForm from '../Login/LoginForm';

// TYPES
import { AccountRegistrationCommand } from '../../api/models';

export interface UserInfoType {
	childAwareId?: string;
	firstName?: string;
	email?: string;
	token?: string;
	organisationIdentifier?: AccountRegistrationCommand['organisationIdentifier'];
	paymentPlanId?: AccountRegistrationCommand['paymentPlanId'];
	profileType?: AccountRegistrationCommand['profileType'];
}

interface ConfirmEmailTypes {}

export const ConfirmEmail: React.FC<ConfirmEmailTypes> = ({}) => {
	const location = useLocation();
	const dispatch = useAppDispatch();

	// Check if user is logged in
	const isUserLoggedIn = useAppSelector((state) => state.user.loggedIn);

	React.useEffect(() => {
		if (isUserLoggedIn) {
			dispatch(UserSlice.actions.logOut());
		}
	}, []);

	const [isLoading, setIsLoading] = React.useState<boolean>(true);
	const [confirmationSuccess, setConfirmationSuccess] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [returnUrl, setReturnUrl] = React.useState<string | undefined>(undefined);

	const [userInfo, setUserInfo] = React.useState<UserInfoType>({
		childAwareId: '',
		firstName: '',
		email: '',
		token: '',
		organisationIdentifier: '',
		paymentPlanId: undefined,
		profileType: undefined
	});

	const decideReturnUrl = () => {
		let url = undefined;

		if (userInfo.organisationIdentifier && !userInfo.profileType) {
			url = `/find-organisation?id=${userInfo.organisationIdentifier}`;
		} else if (userInfo.paymentPlanId) {
			url = '/register-organisation';
		} else if (userInfo.profileType) {
			url = `/find-organisation?id=${userInfo.organisationIdentifier}&type=${userInfo.profileType}`;
		}

		setReturnUrl(url);
	};

	const confirmToken = async () => {
		const locationParams = location.state;
		const queryParams = queryStringToObject();
		const params = { ...locationParams, ...queryParams };

		setIsLoading(true);

		const data = {
			...userInfo,
			email: params.email || '',
			token: params.token || '',
			organisationIdentifier: params.organisationIdentifier || '',
			paymentPlanId: params.paymentPlanId || undefined,
			profileType: params.profileType || undefined
		};

		if (data.token && data.email) {
			const fields = {
				email: params.email,
				token: params.token
			};

			const response = await apibridge.doEmailConfirmation(fields);

			if (response.isError) {
				setFormErrorMessage('An error has occurred \nPlease check your link and try again');
			} else if (response.result) {
				setUserInfo({
					...data,
					childAwareId: response.result.childAwareId,
					firstName: response.result.firstName
				});

				setConfirmationSuccess(true);
			}
		} else {
			setFormErrorMessage('An error has occurred \nPlease check your link and try again');

			setConfirmationSuccess(false);
		}

		setIsLoading(false);
	};

	React.useEffect(() => {
		decideReturnUrl();
	}, [userInfo]);

	React.useEffect(() => {
		confirmToken();
	}, []);

	return (
		<div className="page-confirm-email">
			<div className="standalone-panel">
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : !confirmationSuccess ? (
							<div className="confirm-error">
								<AlertIcon className="icon" />

								<p>{formErrorMessage}</p>
							</div>
						) : (
							<div className="panel panel-login">
								<div className="header">
									<CelebrateIcon className="icon" />

									<h1 className="h1">Welcome{!!userInfo.firstName && ' ' + userInfo.firstName + '!'}</h1>
								</div>

								{!!userInfo.childAwareId && (
									<div className="info-box-wrapper">
										<div className="info-box">
											Your Child<em>Aware</em> ID is <em>{userInfo.childAwareId}</em>
										</div>

										<p className="note">Your ID has been emailed to you</p>
									</div>
								)}

								<LoginForm
									organisationIdentifier={userInfo.organisationIdentifier}
									paymentPlanId={userInfo.paymentPlanId}
									profileType={userInfo.profileType}
									returnUrl={returnUrl}
									emailAddress={userInfo.email}
								/>
							</div>
						)}
					</CSSTransition>
				</SwitchTransition>
			</div>
		</div>
	);
};

export default ConfirmEmail;
